import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box, Plus, RefreshCw, ChevronRight, ChevronDown,
  FolderTree, MapPin, Download, Upload, FileSpreadsheet,
  CheckCircle2, AlertCircle, Pencil, Trash2,
  Boxes, Cloud, ExternalLink,
} from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../components/ui/DataTable";
import { Button } from "../components/ui/Button";
import { StatusBadge } from "../components/ui/StatusBadge";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import { Skeleton } from "../components/ui/LoadingSpinner";
import { Search } from "lucide-react";
import api from "../lib/api";
import { useAppStore } from "../stores/appStore";
import { formatDate } from "../lib/utils";
import { cn } from "../lib/utils";

interface Asset {
  id: string;
  name: string;
  asset_code: string;
  description?: string;
  asset_type?: string;
  system?: string;
  status: string;
  risk_score?: number;
  functional_location_id?: string;
  created_at: string;
}

/** Minimal projection of a 3D tag — only the fields the assets table needs
 *  to render the "Open in 3D" link column. */
interface AssetTagLink {
  id: string;
  digital_twin_id?: string | null;
  point_cloud_id?: string | null;
}

interface ImportRowError {
  row: number;
  asset_code?: string | null;
  error: string;
}

interface ImportResult {
  created_count: number;
  error_count: number;
  errors: ImportRowError[];
}

interface FlocNode {
  id: string;
  code: string;
  name: string;
  level: number;
  parent_id?: string;
  children?: FlocNode[];
}

// ---------------------------------------------------------------------------
// Functional Location Tree Item
// ---------------------------------------------------------------------------
function TreeItem({
  node,
  selectedFlocId,
  onSelect,
  onEdit,
  onDelete,
  depth = 0,
}: {
  node: FlocNode;
  selectedFlocId: string | null;
  onSelect: (id: string | null) => void;
  onEdit: (node: FlocNode) => void;
  onDelete: (node: FlocNode) => void;
  depth?: number;
}) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedFlocId === node.id;

  return (
    <div>
      <div
        className={cn(
          "group flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-sm transition-all hover:bg-surface-700/50",
          isSelected && "bg-brand-500/15 text-brand-300 border border-brand-500/30"
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="p-0.5 hover:bg-surface-600 rounded"
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? (
              <ChevronDown className="h-3 w-3 text-surface-400" />
            ) : (
              <ChevronRight className="h-3 w-3 text-surface-400" />
            )}
          </button>
        ) : (
          <div className="w-4" />
        )}
        <button
          onClick={() => onSelect(isSelected ? null : node.id)}
          className="flex flex-1 min-w-0 items-center gap-1.5 text-left"
        >
          <FolderTree className={cn("h-3.5 w-3.5 shrink-0", isSelected ? "text-brand-400" : "text-surface-500")} />
          <span className={cn("truncate flex-1", isSelected ? "text-brand-200 font-medium" : "text-surface-300")}>
            {node.name}
          </span>
          <span className="text-[10px] text-surface-500 shrink-0">{node.code}</span>
        </button>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(node); }}
            className="p-1 hover:bg-surface-600 rounded text-surface-400 hover:text-surface-200"
            aria-label="Edit functional location"
            title="Edit"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(node); }}
            className="p-1 hover:bg-red-500/20 rounded text-surface-400 hover:text-red-300"
            aria-label="Delete functional location"
            title="Delete"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
      <AnimatePresence>
        {expanded && hasChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            {node.children!.map((child) => (
              <TreeItem
                key={child.id}
                node={child}
                selectedFlocId={selectedFlocId}
                onSelect={onSelect}
                onEdit={onEdit}
                onDelete={onDelete}
                depth={depth + 1}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Asset Register Page
// ---------------------------------------------------------------------------
export default function Assets() {
  const { selectedSiteId } = useAppStore();
  const navigate = useNavigate();

  // Assets state
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  // Search filter is hoisted from DataTable so it can render inline with the
  // header action buttons. DataTable still owns the filtering logic; we just
  // own the input value.
  const [assetSearch, setAssetSearch] = useState("");
  // asset_id → first tag found for that asset (a single asset can only have
  // one tag per the backend's uniqueness rule, so the "first" is the only).
  // Empty entry = checked, no tag exists; missing entry = not yet checked.
  const [assetTags, setAssetTags] = useState<Map<string, AssetTagLink | null>>(new Map());

  // Functional locations state
  const [flocTree, setFlocTree] = useState<FlocNode[]>([]);
  const [flocLoading, setFlocLoading] = useState(true);
  const [selectedFlocId, setSelectedFlocId] = useState<string | null>(null);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "", asset_code: "", description: "", asset_type: "pump", system: "mechanical",
    functional_location_id: "",
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Inline FL creation inside the asset modal.
  const [showInlineFlocForm, setShowInlineFlocForm] = useState(false);
  const [inlineFlocForm, setInlineFlocForm] = useState({ code: "", name: "" });
  const [isCreatingInlineFloc, setIsCreatingInlineFloc] = useState(false);

  // FL create/edit modal (driven from the sidebar)
  const [showFlocModal, setShowFlocModal] = useState(false);
  const [flocModalMode, setFlocModalMode] = useState<"create" | "edit">("create");
  const [flocEditing, setFlocEditing] = useState<FlocNode | null>(null);
  const [flocForm, setFlocForm] = useState({
    code: "", name: "", description: "", parent_id: "",
  });
  const [isSavingFloc, setIsSavingFloc] = useState(false);
  const [flocError, setFlocError] = useState<string | null>(null);

  // FL delete confirm
  const [flocToDelete, setFlocToDelete] = useState<FlocNode | null>(null);
  const [isDeletingFloc, setIsDeletingFloc] = useState(false);
  const [deleteFlocError, setDeleteFlocError] = useState<string | null>(null);

  // Import modal
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  // Fetch functional location tree
  const fetchFlocs = useCallback(async () => {
    if (!selectedSiteId) { setFlocTree([]); setFlocLoading(false); return; }
    setFlocLoading(true);
    try {
      const res = await api.get("/functional-locations", {
        params: { virtual_site_id: selectedSiteId, top_level_only: true, page_size: 200 },
      });
      const topLevel = res.data.items || [];
      // Fetch children for each top-level node
      const tree = await Promise.all(
        topLevel.map(async (node: FlocNode) => {
          try {
            const treeRes = await api.get(`/functional-locations/${node.id}/tree`);
            return treeRes.data;
          } catch {
            return { ...node, children: [] };
          }
        })
      );
      setFlocTree(tree);
    } catch {
      setFlocTree([]);
    } finally {
      setFlocLoading(false);
    }
  }, [selectedSiteId]);

  // Fetch assets
  const fetchAssets = useCallback(async () => {
    if (!selectedSiteId) { setAssets([]); setIsLoading(false); return; }
    setIsLoading(true);
    try {
      const params: Record<string, string | number> = {
        virtual_site_id: selectedSiteId,
        page,
        page_size: 20,
      };
      if (selectedFlocId) params.functional_location_id = selectedFlocId;

      const res = await api.get("/assets", { params });
      setAssets(res.data.items || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch {
      setAssets([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSiteId, selectedFlocId, page]);

  useEffect(() => { fetchFlocs(); }, [fetchFlocs]);
  useEffect(() => { fetchAssets(); }, [fetchAssets]);
  useEffect(() => { setPage(1); }, [selectedFlocId]);

  // After the assets table loads, look up each row's 3D tag (if any) in
  // parallel so the "3D Tag" column can render a deep link. Page size is 20
  // so this is a bounded burst, not unbounded N+1. Cancelled assets between
  // fetches via the local `aborted` flag.
  useEffect(() => {
    if (assets.length === 0) {
      setAssetTags(new Map());
      return;
    }
    let aborted = false;
    (async () => {
      const results = await Promise.allSettled(
        assets.map((a) =>
          api
            .get("/tags", { params: { asset_id: a.id, page_size: 1 } })
            .then((res) => {
              const items = (res.data?.items || res.data || []) as AssetTagLink[];
              return [a.id, items.length > 0 ? items[0] : null] as const;
            }),
        ),
      );
      if (aborted) return;
      const next = new Map<string, AssetTagLink | null>();
      for (const r of results) {
        if (r.status === "fulfilled") next.set(r.value[0], r.value[1]);
      }
      setAssetTags(next);
    })();
    return () => {
      aborted = true;
    };
  }, [assets]);

  async function handleDownloadTemplate() {
    setIsDownloadingTemplate(true);
    try {
      const res = await api.get("/assets/import/template", { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "asset_import_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      // Surface the failure in the import modal so the user notices it.
      setImportError(error.response?.data?.detail || "Failed to download template");
      setShowImport(true);
    } finally {
      setIsDownloadingTemplate(false);
    }
  }

  async function handleImport() {
    if (!selectedSiteId) {
      setImportError("Please select a site first");
      return;
    }
    if (!importFile) {
      setImportError("Choose an .xlsx file to import");
      return;
    }
    setIsImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", importFile);
      const params: Record<string, string> = { virtual_site_id: selectedSiteId };
      if (selectedFlocId) params.default_functional_location_id = selectedFlocId;

      const res = await api.post("/assets/import", form, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      // Refresh the table — even partial imports created some rows.
      if (res.data.created_count > 0) fetchAssets();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setImportError(error.response?.data?.detail || "Import failed");
    } finally {
      setIsImporting(false);
    }
  }

  function resetImport() {
    setShowImport(false);
    setImportFile(null);
    setImportError(null);
    setImportResult(null);
  }

  async function handleCreate() {
    if (!selectedSiteId) {
      setCreateError("Please select a site first");
      return;
    }
    setIsCreating(true);
    setCreateError(null);
    try {
      // FL precedence: explicit choice in the modal > the FL highlighted in
      // the sidebar tree > server-side fallback to "General Assets".
      const flocId = createForm.functional_location_id || selectedFlocId || undefined;
      await api.post("/assets", null, {
        params: {
          virtual_site_id: selectedSiteId,
          name: createForm.name,
          asset_code: createForm.asset_code,
          asset_type: createForm.asset_type,
          system: createForm.system,
          description: createForm.description || undefined,
          functional_location_id: flocId,
        },
      });
      setShowCreate(false);
      setCreateForm({
        name: "", asset_code: "", description: "",
        asset_type: "pump", system: "mechanical", functional_location_id: "",
      });
      setShowInlineFlocForm(false);
      setInlineFlocForm({ code: "", name: "" });
      fetchAssets();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setCreateError(error.response?.data?.detail || "Failed to create asset");
    } finally {
      setIsCreating(false);
    }
  }

  // Flatten the FL tree into an option list (preserving hierarchy via indent).
  function flattenFlocs(nodes: FlocNode[], depth = 0): { id: string; label: string; level: number }[] {
    const out: { id: string; label: string; level: number }[] = [];
    for (const n of nodes) {
      out.push({ id: n.id, label: `${"— ".repeat(depth)}${n.name} (${n.code})`, level: depth });
      if (n.children?.length) out.push(...flattenFlocs(n.children, depth + 1));
    }
    return out;
  }
  const flatFlocs = flattenFlocs(flocTree);

  // Create an FL inline from the asset modal and auto-select it.
  async function handleCreateInlineFloc() {
    if (!selectedSiteId) return;
    if (!inlineFlocForm.code.trim() || !inlineFlocForm.name.trim()) {
      setCreateError("FL code and name are required");
      return;
    }
    setIsCreatingInlineFloc(true);
    setCreateError(null);
    try {
      const res = await api.post("/functional-locations", null, {
        params: {
          virtual_site_id: selectedSiteId,
          code: inlineFlocForm.code.trim(),
          name: inlineFlocForm.name.trim(),
        },
      });
      const newId = res.data.id;
      setCreateForm({ ...createForm, functional_location_id: newId });
      setInlineFlocForm({ code: "", name: "" });
      setShowInlineFlocForm(false);
      fetchFlocs();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setCreateError(error.response?.data?.detail || "Failed to create functional location");
    } finally {
      setIsCreatingInlineFloc(false);
    }
  }

  // ── FL CRUD handlers (sidebar) ───────────────────────────────────────
  function openCreateFloc() {
    setFlocModalMode("create");
    setFlocEditing(null);
    setFlocForm({
      code: "", name: "", description: "",
      parent_id: selectedFlocId || "",
    });
    setFlocError(null);
    setShowFlocModal(true);
  }

  function openEditFloc(node: FlocNode) {
    setFlocModalMode("edit");
    setFlocEditing(node);
    setFlocForm({
      code: node.code,
      name: node.name,
      description: "",
      parent_id: node.parent_id || "",
    });
    setFlocError(null);
    setShowFlocModal(true);
  }

  async function handleSaveFloc() {
    if (!selectedSiteId) return;
    if (!flocForm.code.trim() || !flocForm.name.trim()) {
      setFlocError("Code and name are required");
      return;
    }
    setIsSavingFloc(true);
    setFlocError(null);
    try {
      if (flocModalMode === "create") {
        await api.post("/functional-locations", null, {
          params: {
            virtual_site_id: selectedSiteId,
            code: flocForm.code.trim(),
            name: flocForm.name.trim(),
            description: flocForm.description || undefined,
            parent_id: flocForm.parent_id || undefined,
          },
        });
      } else if (flocEditing) {
        await api.put(`/functional-locations/${flocEditing.id}`, null, {
          params: {
            code: flocForm.code.trim(),
            name: flocForm.name.trim(),
            description: flocForm.description || undefined,
            parent_id: flocForm.parent_id || undefined,
          },
        });
      }
      setShowFlocModal(false);
      fetchFlocs();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setFlocError(error.response?.data?.detail || "Failed to save functional location");
    } finally {
      setIsSavingFloc(false);
    }
  }

  async function handleDeleteFloc() {
    if (!flocToDelete) return;
    setIsDeletingFloc(true);
    setDeleteFlocError(null);
    try {
      await api.delete(`/functional-locations/${flocToDelete.id}`);
      if (selectedFlocId === flocToDelete.id) setSelectedFlocId(null);
      setFlocToDelete(null);
      fetchFlocs();
      fetchAssets();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setDeleteFlocError(error.response?.data?.detail || "Failed to delete functional location");
    } finally {
      setIsDeletingFloc(false);
    }
  }

  const columns: ColumnDef<Asset, unknown>[] = [
    {
      accessorKey: "name",
      header: "Asset",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-500/15">
            <Box className="h-4 w-4 text-brand-400" />
          </div>
          <div>
            <p className="font-medium text-surface-100">{row.original.name}</p>
            <p className="text-xs text-surface-500">{row.original.asset_code}</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "asset_type",
      header: "Type",
      cell: ({ row }) => (
        <span className="text-surface-300 text-sm capitalize">
          {(row.original.asset_type || "-").replace(/_/g, " ")}
        </span>
      ),
    },
    {
      accessorKey: "system",
      header: "System",
      cell: ({ row }) => (
        <span className="text-surface-400 text-sm capitalize">
          {(row.original.system || "-").replace(/_/g, " ")}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <StatusBadge status={row.original.status || "unknown"} />,
    },
    {
      accessorKey: "risk_score",
      header: "Risk",
      cell: ({ row }) => {
        const score = row.original.risk_score;
        if (score == null) return <span className="text-surface-500 text-sm">-</span>;
        const color = score >= 70 ? "text-red-400" : score >= 40 ? "text-yellow-400" : "text-green-400";
        return <span className={cn("text-sm font-semibold", color)}>{score.toFixed(0)}</span>;
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-surface-400 text-sm">{formatDate(row.original.created_at)}</span>
      ),
    },
    {
      id: "tag_link",
      header: "3D Tag",
      cell: ({ row }) => {
        const tag = assetTags.get(row.original.id);
        // Map not yet populated for this row — the per-asset tag lookup is
        // still in flight. Show a dim em-dash placeholder rather than "none"
        // so the user doesn't see false negatives flash before the data lands.
        if (tag === undefined) {
          return <span className="text-surface-600 text-sm">…</span>;
        }
        if (!tag) {
          return <span className="text-surface-500 text-sm">—</span>;
        }
        const isTwin = !!tag.digital_twin_id;
        const href = isTwin
          ? `/digital-twins/${tag.digital_twin_id}?tag=${tag.id}`
          : `/point-clouds/${tag.point_cloud_id}?tag=${tag.id}`;
        const Icon = isTwin ? Boxes : Cloud;
        const colorClass = isTwin ? "text-purple-400" : "text-blue-400";
        return (
          <button
            onClick={(e) => {
              // Row click also navigates (to the asset detail), so stop the
              // event from bubbling — clicking the tag link should jump to
              // the 3D viewer, not the asset detail.
              e.stopPropagation();
              navigate(href);
            }}
            title={isTwin ? "Open in digital twin viewer" : "Open in point cloud viewer"}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border border-surface-700/60 bg-surface-800/60 px-2 py-1 text-xs font-medium transition-colors hover:bg-surface-700",
              colorClass,
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            Open in 3D
            <ExternalLink className="h-3 w-3 opacity-70" />
          </button>
        );
      },
    },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      {/* Header — title on the left, then a single flex row containing the
          search input followed by the action buttons. This keeps the FL
          sidebar and the assets table starting at the same Y position (since
          the DataTable no longer renders its own search row above the table). */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Asset Register</h1>
          <p className="text-sm text-surface-400 mt-1">
            Browse assets by functional location hierarchy
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-64">
            <Input
              placeholder="Search assets..."
              value={assetSearch}
              onChange={(e) => setAssetSearch(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>
          <Button variant="secondary" size="icon" onClick={() => { fetchFlocs(); fetchAssets(); }}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="secondary" onClick={handleDownloadTemplate} isLoading={isDownloadingTemplate}>
            <Download className="h-4 w-4" />
            Template
          </Button>
          <Button variant="secondary" onClick={() => setShowImport(true)}>
            <Upload className="h-4 w-4" />
            Import XLSX
          </Button>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" />
            Add Asset
          </Button>
        </div>
      </div>

      {/* Main content: tree + table */}
      <div className="flex gap-4">
        {/* FL Tree sidebar */}
        <div className="w-72 shrink-0 rounded-xl border border-surface-700/50 bg-surface-800/40 backdrop-blur overflow-hidden">
          <div className="flex items-start gap-2 px-4 py-3 border-b border-surface-700/50">
            <FolderTree className="h-4 w-4 text-brand-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-surface-200">Functional Locations</div>
              <div className="text-[11px] text-surface-500">Grouped Assets</div>
            </div>
            {selectedFlocId && (
              <button
                onClick={() => setSelectedFlocId(null)}
                className="text-xs text-brand-400 hover:text-brand-300"
                title="Clear selection"
              >
                Clear
              </button>
            )}
            <button
              onClick={openCreateFloc}
              className="p-1 rounded hover:bg-surface-700 text-surface-300 hover:text-brand-300"
              title="Add functional location"
              aria-label="Add functional location"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="p-2 max-h-[calc(100vh-240px)] overflow-y-auto">
            {flocLoading ? (
              <div className="space-y-2 p-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-6 w-5/6" />
                <Skeleton className="h-6 w-2/3" />
              </div>
            ) : flocTree.length === 0 ? (
              <div className="py-8 text-center">
                <MapPin className="h-8 w-8 text-surface-600 mx-auto mb-2" />
                <p className="text-sm text-surface-500">No locations defined</p>
                <p className="text-xs text-surface-600 mt-1">Select a site first</p>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setSelectedFlocId(null)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-all hover:bg-surface-700/50 mb-1",
                    !selectedFlocId && "bg-brand-500/15 text-brand-300 border border-brand-500/30"
                  )}
                >
                  <Box className="h-3.5 w-3.5 text-surface-500" />
                  <span className={cn(!selectedFlocId ? "text-brand-200 font-medium" : "text-surface-300")}>
                    All Assets
                  </span>
                  <span className="ml-auto text-xs text-surface-500">{total}</span>
                </button>
                {flocTree.map((node) => (
                  <TreeItem
                    key={node.id}
                    node={node}
                    selectedFlocId={selectedFlocId}
                    onSelect={setSelectedFlocId}
                    onEdit={openEditFloc}
                    onDelete={(n) => { setDeleteFlocError(null); setFlocToDelete(n); }}
                  />
                ))}
              </>
            )}
          </div>
        </div>

        {/* Assets table */}
        <div className="flex-1 min-w-0">
          <DataTable
            columns={columns}
            data={assets}
            isLoading={isLoading}
            enableSearch={false}
            searchValue={assetSearch}
            onSearchChange={setAssetSearch}
            emptyMessage={
              selectedFlocId
                ? "No assets in this location."
                : "No assets found. Create your first asset to get started."
            }
            onRowClick={(row) => navigate(`/assets/${row.id}`)}
            page={page}
            totalPages={totalPages}
            total={total}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* Create modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => { setShowCreate(false); setCreateError(null); }}
        title="Create Asset"
      >
        <div className="space-y-4">
          {createError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {createError}
            </div>
          )}
          <Input
            label="Asset Name"
            placeholder="e.g., Pump P-101"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            required
          />
          <Input
            label="Asset Code"
            placeholder="e.g., P-101"
            value={createForm.asset_code}
            onChange={(e) => setCreateForm({ ...createForm, asset_code: e.target.value })}
            required
          />
          {/* Functional location selector + inline create */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-surface-300">Functional Location</label>
              <button
                type="button"
                onClick={() => setShowInlineFlocForm((v) => !v)}
                className="text-xs text-brand-400 hover:text-brand-300"
              >
                {showInlineFlocForm ? "Cancel" : "+ New FL"}
              </button>
            </div>
            {!showInlineFlocForm ? (
              <select
                value={createForm.functional_location_id || (selectedFlocId ?? "")}
                onChange={(e) => setCreateForm({ ...createForm, functional_location_id: e.target.value })}
                className="w-full rounded-lg border border-surface-600 bg-surface-700 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none"
              >
                <option value="">— General Assets (default) —</option>
                {flatFlocs.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            ) : (
              <div className="space-y-2 rounded-lg border border-brand-500/30 bg-brand-500/5 p-3">
                <Input
                  label="FL Code"
                  placeholder="e.g., AREA-100"
                  value={inlineFlocForm.code}
                  onChange={(e) => setInlineFlocForm({ ...inlineFlocForm, code: e.target.value })}
                />
                <Input
                  label="FL Name"
                  placeholder="e.g., Process Area 100"
                  value={inlineFlocForm.name}
                  onChange={(e) => setInlineFlocForm({ ...inlineFlocForm, name: e.target.value })}
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleCreateInlineFloc}
                    isLoading={isCreatingInlineFloc}
                    disabled={!inlineFlocForm.code.trim() || !inlineFlocForm.name.trim()}
                  >
                    Create FL & Use
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1.5">Type</label>
              <select
                value={createForm.asset_type}
                onChange={(e) => setCreateForm({ ...createForm, asset_type: e.target.value })}
                className="w-full rounded-lg border border-surface-600 bg-surface-700 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none"
              >
                {["pump", "valve", "compressor", "heat_exchanger", "tank", "pipe", "instrument", "motor", "conveyor", "other"].map((t) => (
                  <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1.5">System</label>
              <select
                value={createForm.system}
                onChange={(e) => setCreateForm({ ...createForm, system: e.target.value })}
                className="w-full rounded-lg border border-surface-600 bg-surface-700 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none"
              >
                {["mechanical", "electrical", "instrumentation", "civil", "hvac"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <Input
            label="Description"
            placeholder="Optional description"
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => { setShowCreate(false); setCreateError(null); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} isLoading={isCreating}>
              Create Asset
            </Button>
          </div>
        </div>
      </Modal>

      {/* Import XLSX modal */}
      <Modal
        isOpen={showImport}
        onClose={resetImport}
        title="Import Assets from XLSX"
      >
        <div className="space-y-4">
          {importError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {importError}
            </div>
          )}

          {!importResult && (
            <>
              <div className="rounded-lg bg-surface-700/40 border border-surface-600/50 px-3 py-2 text-xs text-surface-300 space-y-1">
                <p>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="text-brand-400 hover:text-brand-300 underline"
                  >
                    Download the template
                  </button>{" "}
                  to see the expected columns and allowed enum values.
                </p>
                {selectedFlocId && (
                  <p className="text-brand-300">
                    Rows with a blank <code>functional_location_code</code> will be linked to the selected location.
                  </p>
                )}
              </div>

              <label className="block">
                <span className="block text-sm font-medium text-surface-300 mb-1.5">XLSX file</span>
                <div className="relative">
                  <input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                    className="block w-full text-sm text-surface-300
                               file:mr-3 file:rounded-lg file:border-0
                               file:bg-brand-500/20 file:px-4 file:py-2
                               file:text-sm file:font-medium file:text-brand-200
                               hover:file:bg-brand-500/30 cursor-pointer"
                  />
                </div>
                {importFile && (
                  <p className="mt-2 flex items-center gap-2 text-xs text-surface-400">
                    <FileSpreadsheet className="h-3.5 w-3.5 text-brand-400" />
                    {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="secondary" onClick={resetImport}>Cancel</Button>
                <Button onClick={handleImport} isLoading={isImporting} disabled={!importFile}>
                  Import
                </Button>
              </div>
            </>
          )}

          {importResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3">
                  <div className="flex items-center gap-2 text-green-300">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-xs font-medium">Created</span>
                  </div>
                  <p className="text-2xl font-bold text-green-200 mt-1">{importResult.created_count}</p>
                </div>
                <div className={cn(
                  "rounded-lg border px-4 py-3",
                  importResult.error_count > 0
                    ? "border-red-500/30 bg-red-500/10"
                    : "border-surface-600 bg-surface-700/30",
                )}>
                  <div className={cn(
                    "flex items-center gap-2",
                    importResult.error_count > 0 ? "text-red-300" : "text-surface-400",
                  )}>
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-xs font-medium">Errors</span>
                  </div>
                  <p className={cn(
                    "text-2xl font-bold mt-1",
                    importResult.error_count > 0 ? "text-red-200" : "text-surface-300",
                  )}>
                    {importResult.error_count}
                  </p>
                </div>
              </div>

              {importResult.errors.length > 0 && (
                <div className="rounded-lg border border-surface-600 bg-surface-800/60 max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-surface-700/80 backdrop-blur">
                      <tr className="text-left text-surface-300">
                        <th className="px-3 py-2 w-12">Row</th>
                        <th className="px-3 py-2 w-28">Asset Code</th>
                        <th className="px-3 py-2">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-700/60">
                      {importResult.errors.map((e, i) => (
                        <tr key={i} className="text-surface-300">
                          <td className="px-3 py-2 font-mono text-surface-400">{e.row}</td>
                          <td className="px-3 py-2 font-mono text-surface-400">{e.asset_code || "-"}</td>
                          <td className="px-3 py-2 text-red-300">{e.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="secondary" onClick={() => { setImportResult(null); setImportFile(null); }}>
                  Import Another
                </Button>
                <Button onClick={resetImport}>Done</Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* FL create/edit modal */}
      <Modal
        isOpen={showFlocModal}
        onClose={() => { setShowFlocModal(false); setFlocError(null); }}
        title={flocModalMode === "create" ? "Create Functional Location" : "Edit Functional Location"}
      >
        <div className="space-y-4">
          {flocError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {flocError}
            </div>
          )}
          <Input
            label="Code"
            placeholder="e.g., AREA-100"
            value={flocForm.code}
            onChange={(e) => setFlocForm({ ...flocForm, code: e.target.value })}
            required
          />
          <Input
            label="Name"
            placeholder="e.g., Process Area 100"
            value={flocForm.name}
            onChange={(e) => setFlocForm({ ...flocForm, name: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1.5">Parent (optional)</label>
            <select
              value={flocForm.parent_id}
              onChange={(e) => setFlocForm({ ...flocForm, parent_id: e.target.value })}
              className="w-full rounded-lg border border-surface-600 bg-surface-700 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none"
            >
              <option value="">— Top level —</option>
              {flatFlocs
                .filter((f) => !flocEditing || f.id !== flocEditing.id)
                .map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
            </select>
          </div>
          <Input
            label="Description"
            placeholder="Optional description"
            value={flocForm.description}
            onChange={(e) => setFlocForm({ ...flocForm, description: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => { setShowFlocModal(false); setFlocError(null); }}>
              Cancel
            </Button>
            <Button onClick={handleSaveFloc} isLoading={isSavingFloc}>
              {flocModalMode === "create" ? "Create FL" : "Save Changes"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* FL delete confirm */}
      <Modal
        isOpen={!!flocToDelete}
        onClose={() => { setFlocToDelete(null); setDeleteFlocError(null); }}
        title="Delete Functional Location"
      >
        <div className="space-y-4">
          {deleteFlocError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {deleteFlocError}
            </div>
          )}
          <p className="text-sm text-surface-300">
            Delete <span className="font-medium text-surface-100">{flocToDelete?.name}</span>{" "}
            <span className="text-surface-500">({flocToDelete?.code})</span>?
          </p>
          <p className="text-xs text-surface-500">
            This fails if the FL still has child locations or linked assets — move or
            delete those first.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => { setFlocToDelete(null); setDeleteFlocError(null); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteFloc} isLoading={isDeletingFloc}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
